import React, { useState, useRef, useEffect } from 'react';
import styles from './style.module.css';
import { useNavigate } from 'react-router-dom';
import Typography from '../../../../components/Typography/Typography';
import { 
  useGetCurrentUserQuery, 
  useUpdateUserProfileMutation,
  useUpdateUserAvatarMutation 
} from '../../../../redux/services/userApi';

interface ProfileFormData {
  display_name: string;
  email: string;
  phone: string;
  bio: string;
  street_name: string;
  city_or_state: string;
  postal_code: string;
  country: string;
  full_address: string;
  additional_info: string;
}

export const EditSellerProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  const defaultAvatarImage = 'https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?q=80&w=200&h=200&auto=format&fit=crop';

  const {
    data: userData,
    isLoading,
    error,
    refetch,
  } = useGetCurrentUserQuery('');

  const [updateProfile, { isLoading: isUpdatingProfile }] = useUpdateUserProfileMutation();
  const [updateAvatar, { isLoading: isUpdatingAvatar }] = useUpdateUserAvatarMutation();

  const [formData, setFormData] = useState<ProfileFormData>({
    display_name: '',
    email: '',
    phone: '',
    bio: '',
    street_name: '',
    city_or_state: '',
    postal_code: '',
    country: '',
    full_address: '',
    additional_info: '',
  });

  useEffect(() => {
    if (userData) {
      setFormData({
        display_name: userData.display_name || '',
        email: userData.email || '',
        phone: userData.phone || '',
        bio: userData.bio || '',
        street_name: userData.street_name || '',
        city_or_state: userData.city_or_state || '',
        postal_code: userData.postal_code || '',
        country: userData.country || '',
        full_address: userData.full_address || '',
        additional_info: userData.additional_info || '',
      });
      setAvatarPreview(userData.photo_url || defaultAvatarImage);
    }
  }, [userData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }
      
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      if (avatarFile) {
        const avatarFormData = new FormData();
        avatarFormData.append('avatar_file', avatarFile);
        
        const avatarResult = await updateAvatar(avatarFormData).unwrap();
        console.log('Avatar updated:', avatarResult);
      }

      const updateData: Record<string, string> = {};
      
      if (formData.display_name?.trim()) updateData.display_name = formData.display_name.trim();
      if (formData.email?.trim()) updateData.email = formData.email.trim();
      if (formData.phone?.trim()) updateData.phone = formData.phone.trim();
      if (formData.street_name?.trim()) updateData.street_name = formData.street_name.trim();
      if (formData.city_or_state?.trim()) updateData.city_or_state = formData.city_or_state.trim();
      if (formData.postal_code?.trim()) updateData.postal_code = formData.postal_code.trim();
      if (formData.country?.trim()) updateData.country = formData.country.trim();
      if (formData.full_address?.trim()) updateData.full_address = formData.full_address.trim();
      if (formData.additional_info?.trim()) updateData.additional_info = formData.additional_info.trim();

      console.log('Sending update data:', updateData);

      if (Object.keys(updateData).length > 0) {
        const profileResult = await updateProfile(updateData).unwrap();
        console.log('Profile updated:', profileResult);
      }

      alert('Profile updated successfully!');
      
      await refetch();
      
      await new Promise(resolve => setTimeout(resolve, 300));
      
      navigate('/profile', { replace: true, state: { timestamp: Date.now() } });
    } catch (error: any) {
      console.error('Error updating profile:', error);
      console.error('Error details:', error?.data);
      
      if (error?.status === 401) {
        alert('Your session has expired. Please login again.');
        navigate('/signIn');
      } else if (error?.status === 422) {
        const detail = error?.data?.detail;
        if (Array.isArray(detail)) {
          const errorMessages = detail.map((err: any) => `${err.loc?.join(' → ')}: ${err.msg}`).join('\n');
          alert(`Validation Error:\n${errorMessages}`);
        } else if (typeof detail === 'string') {
          alert(`Validation Error: ${detail}`);
        } else {
          alert('Invalid data format. Please check your inputs.');
        }
      } else if (error?.data?.detail) {
        alert(`Error: ${error.data.detail}`);
      } else {
        alert('Failed to update profile. Please try again.');
      }
    }
  };

  const isSubmitting = isUpdatingProfile || isUpdatingAvatar;

  if (isLoading) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.editProfileContainer}>
          <Typography variant="heading5" fontFamily="satoshi" className={styles.loadingMessage}>
            Loading...
          </Typography>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.editProfileContainer}>
          <Typography variant="heading5" fontFamily="satoshi" className={styles.errorMessage}>
            Error loading profile data
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.editProfileContainer}>
        <div className={styles.avatarContainer}>
          <img
            src={avatarPreview}
            alt="Profile Avatar"
            className={styles.avatar}
            onClick={handleAvatarClick}
          />
          <button 
            type="button" 
            className={styles.avatarOverlay}
            onClick={handleAvatarClick}
            aria-label="Change profile picture"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 3H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 17C14.2091 17 16 15.2091 16 13C16 10.7909 14.2091 9 12 9C9.79086 9 8 10.7909 8 13C8 15.2091 9.79086 17 12 17Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleAvatarChange}
            className={styles.hiddenInput}
            aria-label="Upload profile picture"
          />
        </div>

        <form onSubmit={handleSubmit} className={styles.profileForm}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="full_name" className={styles.formLabel}>
                <Typography variant="bodyRegular" fontFamily="satoshi">
                  Full Name
                </Typography>
              </label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={userData?.full_name || ''}
                className={styles.formInput}
                disabled
                placeholder="Full Name"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="display_name" className={styles.formLabel}>
                <Typography variant="bodyRegular" fontFamily="satoshi">
                  Display Name
                </Typography>
              </label>
              <input
                type="text"
                id="display_name"
                name="display_name"
                value={formData.display_name}
                onChange={handleChange}
                className={styles.formInput}
                placeholder="Display Name"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="email" className={styles.formLabel}>
                <Typography variant="bodyRegular" fontFamily="satoshi">
                  Email Address
                </Typography>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={styles.formInput}
                placeholder="Email Address"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="phone" className={styles.formLabel}>
                <Typography variant="bodyRegular" fontFamily="satoshi">
                  Phone Number
                </Typography>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={styles.formInput}
                placeholder="Phone Number"
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="additional_info" className={styles.formLabel}>
              <Typography variant="bodyRegular" fontFamily="satoshi">
                Bio / Additional Information
              </Typography>
            </label>
            <textarea
              id="additional_info"
              name="additional_info"
              value={formData.additional_info}
              onChange={handleChange}
              className={`${styles.formInput} ${styles.bioTextarea}`}
              placeholder="Tell us about yourself..."

            />
          </div>

          <button 
            type="submit" 
            className={styles.submitButton}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  );
};